import React, { useState, useEffect } from 'react';
import './MapControls.css';

interface PhysicalSize {
  width: number;
  height: number;
  unit: 'inches';
}

interface ViewState {
  centerLat: number;
  centerLong: number;
  zoomLevel: number;
  pixelOffsetX: number;
  pixelOffsetY: number;
}

interface MapControlsProps {
  physicalSize: PhysicalSize;
  orientation: 'portrait' | 'landscape';
  viewState: ViewState;
  collisionCount: number;
  onPhysicalSizeChange: (size: PhysicalSize) => void;
  onOrientationChange: (orientation: 'portrait' | 'landscape') => void;
  onZoomChange: (zoomLevel: number) => void;
  onResetLayout: () => void;
}

// Standard frame sizes in inches (width x height in portrait orientation)
const STANDARD_FRAME_SIZES = [
  { label: '10" × 13"', width: 10, height: 13 },
  { label: '10" × 20"', width: 10, height: 20 },
  { label: '11" × 14"', width: 11, height: 14 },
  { label: '11" × 17"', width: 11, height: 17 },
  { label: '12" × 12"', width: 12, height: 12 },
  { label: '12" × 16"', width: 12, height: 16 },
  { label: '12" × 18"', width: 12, height: 18 },
  { label: '16" × 20"', width: 16, height: 20 },
  { label: '18" × 24"', width: 18, height: 24 },
  { label: '20" × 24"', width: 20, height: 24 },
  { label: '20" × 30"', width: 20, height: 30 },
  { label: '24" × 36"', width: 24, height: 36 },
  { label: '27" × 40"', width: 27, height: 40 },
  { label: '28" × 40"', width: 28, height: 40 },
  { label: '30" × 40"', width: 30, height: 40 },
  { label: '36" × 48"', width: 36, height: 48 },
];

export const MapControls: React.FC<MapControlsProps> = ({
  physicalSize,
  orientation,
  viewState,
  collisionCount,
  onPhysicalSizeChange,
  onOrientationChange,
  onZoomChange,
  onResetLayout,
}) => {
  const [selectedFrameSize, setSelectedFrameSize] = useState<string>('custom');
  const [customWidth, setCustomWidth] = useState<string>(physicalSize.width.toString());
  const [customHeight, setCustomHeight] = useState<string>(physicalSize.height.toString());
  const [widthError, setWidthError] = useState<string>('');
  const [heightError, setHeightError] = useState<string>('');

  // Update custom inputs when physicalSize changes externally
  useEffect(() => {
    setCustomWidth(physicalSize.width.toString());
    setCustomHeight(physicalSize.height.toString());
  }, [physicalSize]);

  // Validate dimension input
  const validateDimension = (value: string, fieldName: string): string => {
    if (value === '') {
      return `${fieldName} is required`;
    }
    
    const num = parseFloat(value);
    
    if (isNaN(num)) {
      return `${fieldName} must be a number`;
    }
    
    if (num <= 0) {
      return `${fieldName} must be positive`;
    }
    
    if (num > 100) {
      return `${fieldName} cannot exceed 100 inches`;
    }
    
    return '';
  };

  // Handle frame size selection
  const handleFrameSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedFrameSize(value);
    
    if (value === 'custom') {
      return;
    }
    
    const frameSize = STANDARD_FRAME_SIZES.find(
      (size) => `${size.width}x${size.height}` === value
    );
    
    if (frameSize) {
      // Apply orientation to the selected frame size
      const newSize: PhysicalSize = {
        width: orientation === 'portrait' ? frameSize.width : frameSize.height,
        height: orientation === 'portrait' ? frameSize.height : frameSize.width,
        unit: 'inches',
      };
      
      onPhysicalSizeChange(newSize);
      setCustomWidth(newSize.width.toString());
      setCustomHeight(newSize.height.toString());
      setWidthError('');
      setHeightError('');
    }
  };

  // Handle orientation change
  const handleOrientationChange = (newOrientation: 'portrait' | 'landscape') => {
    if (newOrientation === orientation) return;
    
    // Swap width and height
    const newSize: PhysicalSize = {
      width: physicalSize.height,
      height: physicalSize.width,
      unit: 'inches',
    };
    
    onPhysicalSizeChange(newSize);
    onOrientationChange(newOrientation);
    
    // Update custom inputs
    setCustomWidth(newSize.width.toString());
    setCustomHeight(newSize.height.toString());
  };

  // Handle custom width change
  const handleCustomWidthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCustomWidth(value);
    setSelectedFrameSize('custom');
    
    const error = validateDimension(value, 'Width');
    setWidthError(error);
    
    if (!error && !heightError) {
      const width = parseFloat(value);
      const height = parseFloat(customHeight);
      
      onPhysicalSizeChange({
        width,
        height,
        unit: 'inches',
      });
    }
  };

  // Handle custom height change
  const handleCustomHeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCustomHeight(value);
    setSelectedFrameSize('custom');
    
    const error = validateDimension(value, 'Height');
    setHeightError(error);
    
    if (!error && !widthError) {
      const width = parseFloat(customWidth);
      const height = parseFloat(value);
      
      onPhysicalSizeChange({
        width,
        height,
        unit: 'inches',
      });
    }
  };

  // Handle zoom controls
  const handleZoomIn = () => {
    const newZoom = Math.min(2000, viewState.zoomLevel + 10);
    onZoomChange(newZoom);
  };

  const handleZoomOut = () => {
    const newZoom = Math.max(10, viewState.zoomLevel - 10);
    onZoomChange(newZoom);
  };

  const handleZoomSlider = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newZoom = parseFloat(e.target.value);
    onZoomChange(newZoom);
  };

  return (
    <div className="map-controls">
      <div className="controls-section">
        <h3>Physical Map Size</h3>
        
        {/* Frame Size Dropdown */}
        <div className="control-group">
          <label htmlFor="frame-size">Frame Size:</label>
          <select
            id="frame-size"
            value={selectedFrameSize}
            onChange={handleFrameSizeChange}
            className="frame-size-select"
          >
            <option value="custom">Custom Size</option>
            {STANDARD_FRAME_SIZES.map((size) => (
              <option key={`${size.width}x${size.height}`} value={`${size.width}x${size.height}`}>
                {size.label}
              </option>
            ))}
          </select>
        </div>

        {/* Orientation Selector */}
        <div className="control-group">
          <label>Orientation:</label>
          <div className="orientation-buttons">
            <button
              className={`orientation-btn ${orientation === 'portrait' ? 'active' : ''}`}
              onClick={() => handleOrientationChange('portrait')}
            >
              Portrait
            </button>
            <button
              className={`orientation-btn ${orientation === 'landscape' ? 'active' : ''}`}
              onClick={() => handleOrientationChange('landscape')}
            >
              Landscape
            </button>
          </div>
        </div>

        {/* Custom Dimensions */}
        <div className="control-group">
          <label htmlFor="custom-width">Width (inches):</label>
          <input
            id="custom-width"
            type="number"
            min="0.1"
            max="100"
            step="0.1"
            value={customWidth}
            onChange={handleCustomWidthChange}
            className={`dimension-input ${widthError ? 'error' : ''}`}
          />
          {widthError && <span className="error-message">{widthError}</span>}
        </div>

        <div className="control-group">
          <label htmlFor="custom-height">Height (inches):</label>
          <input
            id="custom-height"
            type="number"
            min="0.1"
            max="100"
            step="0.1"
            value={customHeight}
            onChange={handleCustomHeightChange}
            className={`dimension-input ${heightError ? 'error' : ''}`}
          />
          {heightError && <span className="error-message">{heightError}</span>}
        </div>

        <div className="size-display">
          Current Size: {physicalSize.width}" × {physicalSize.height}"
        </div>
      </div>

      <div className="controls-section">
        <h3>Zoom Controls</h3>
        
        <div className="zoom-controls">
          <button onClick={handleZoomOut} className="zoom-btn">−</button>
          <input
            type="range"
            min="10"
            max="2000"
            step="1"
            value={viewState.zoomLevel}
            onChange={handleZoomSlider}
            className="zoom-slider"
          />
          <button onClick={handleZoomIn} className="zoom-btn">+</button>
        </div>
        
        <div className="zoom-display">
          Zoom: {Math.round(viewState.zoomLevel)}%
        </div>

        <button onClick={onResetLayout} className="reset-btn">
          Reset to Auto-Layout
        </button>
      </div>

      {/* Collision Warning */}
      {collisionCount > 0 && (
        <div className="controls-section collision-warning">
          <h3>⚠️ LED Collisions Detected</h3>
          <p>
            {collisionCount} collision{collisionCount !== 1 ? 's' : ''} detected. 
            LEDs are too close together for physical installation.
          </p>
          <p className="collision-help">
            Adjust zoom level or reposition locations to resolve collisions.
          </p>
        </div>
      )}
    </div>
  );
};
